export class DateUtils {
  /**
   * Converte um horário no formato de 12 horas (ex: "9am", "9pm") para o formato de 24 horas ("HH:mm").
   * Também trata o caso especial de "now", retornando o horário atual no formato de 24 horas.
   *
   * Formatos aceitos:
   * - "now": retorna a hora atual no formato "HH:mm".
   * - "hham/pm": converte o horário no formato de 12 horas (ex: "9am", "3pm").
   * - "HH:mm": se já estiver no formato de 24 horas, retorna o valor diretamente.
   *
   * @param timeString A string de hora no formato "hham" ou "hhpm", ou "now" para a hora atual.
   * @returns A hora no formato 24 horas ("HH:mm").
   * @throws {Error} Se o formato do horário for inválido.
   */
  static convertTo24HourFormat(timeString: string): string {
    if (timeString.toLowerCase() === "now") {
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      return `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
    }

    const is24HourFormat = timeString.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
    if (is24HourFormat) {
      return timeString;
    }

    const match = timeString.match(/^(\d{1,2})(am|pm)$/i);
    if (!match) {
      throw new Error("Formato de horário inválido");
    }

    const [, hourString, period] = match;
    let hour = parseInt(hourString, 10);

    if (period.toLowerCase() === "pm" && hour < 12) {
      hour += 12;
    } else if (period.toLowerCase() === "am" && hour === 12) {
      hour = 0;
    }

    const formattedHour = hour.toString().padStart(2, "0");
    return `${formattedHour}:00`;
  }

  /**
   * Converte uma string de data e hora no formato "YYYY-MM-DD hh am/pm" para o formato ISO 8601 ("YYYY-MM-DDTHH:mm").
   * Também trata o caso especial de "now", retornando a data e hora atuais no formato ISO 8601.
   *
   * Formatos aceitos:
   * - "now": retorna a data e hora atual no formato "YYYY-MM-DDTHH:mm".
   * - "YYYY-MM-DD hh am/pm": converte a data e hora de 12 horas para o formato ISO 8601.
   * - "YYYY-MM-DDTHH:mm" ou "YYYY-MM-DDTHH:mm:ss": se já estiver no formato ISO 8601, retorna diretamente.
   *
   * @param datetimeString A string de data e hora no formato "YYYY-MM-DD hh am/pm", ou "now" para a data e hora atuais.
   * @returns A data e hora no formato ISO 8601 ("YYYY-MM-DDTHH:mm").
   * @throws {Error} Se o formato de data e horário for inválido.
   */
  static convertToDateTimeFormat(datetimeString: string): string {
    if (datetimeString.toLowerCase() === "now") {
      const now = new Date();
      const year = now.getFullYear();
      const month = (now.getMonth() + 1).toString().padStart(2, "0");
      const day = now.getDate().toString().padStart(2, "0");
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    const isISOFormat = datetimeString.match(
      /^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/
    );
    if (isISOFormat) {
      return datetimeString;
    }

    const match = datetimeString.match(
      /^(\d{4}-\d{2}-\d{2}) (\d{1,2})(am|pm)$/i
    );

    if (!match) {
      throw new Error("Formato de data e horário inválido");
    }

    const [, dateString, hourString, period] = match;
    let hour = parseInt(hourString, 10);

    if (period.toLowerCase() === "pm" && hour < 12) {
      hour += 12;
    } else if (period.toLowerCase() === "am" && hour === 12) {
      hour = 0;
    }

    const formattedHour = hour.toString().padStart(2, "0");
    return `${dateString}T${formattedHour}:00`;
  }

  /**
   * Formata uma data para o formato "YYYY-MM-DD 00:00:00".
   * Utiliza a data atual ou a data fornecida para garantir que o formato retorne sempre no início do dia.
   *
   * @param date A data a ser formatada. Se não for fornecida, será usada a data atual.
   * @returns A data formatada no formato "YYYY-MM-DD 00:00:00".
   */
  static formatDate(date: Date = new Date()): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day} 00:00:00`;
  }
}
